import {
  BankingAnalyticsGroupBy,
  BankingContractAnalytics,
  BankingContractAnalyticsBucket,
  BankingContractPeriodResponse,
  BankingContractReturnAnalytics,
  BankingContractReturnMetrics,
} from "../../../models/banking";
import { Contract, Granularity, PeriodRow, SupplyPoint } from "./types";

export function buildSupplySeries(start: string, end: string): SupplyPoint[] {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const out: SupplyPoint[] = [];
  const cursor = new Date(startDate);
  let idx = 0;
  while (cursor <= endDate) {
    const isPlateau = idx < 16;
    const scheduled = isPlateau ? 5500 : 2200;
    const noise = (Math.sin(idx * 0.9) + Math.cos(idx * 0.4)) * 60;
    const actual = Math.max(0, scheduled - 180 + noise);
    out.push({
      date: cursor.toISOString().slice(5, 10),
      scheduled,
      actual: Math.round(actual),
    });
    cursor.setDate(cursor.getDate() + 1);
    idx += 1;
  }
  return out;
}

export function buildPeriodRows(series: SupplyPoint[], year: string): PeriodRow[] {
  return series.map((s) => {
    const delta = s.actual - s.scheduled;
    const pct = (delta / s.scheduled) * 100;
    return {
      date: `${year}-${s.date}`,
      scheduled: s.scheduled,
      actual: s.actual,
      delta,
      pct,
    };
  });
}

export type Totals = {
  actual: number;
  scheduled: number;
  varPct: number;
  days: number;
};

export function computeTotals(series: SupplyPoint[]): Totals {
  const actual = series.reduce((s, r) => s + r.actual, 0);
  const scheduled = series.reduce((s, r) => s + r.scheduled, 0);
  const varPct = scheduled === 0 ? 0 : ((actual - scheduled) / scheduled) * 100;
  return { actual, scheduled, varPct, days: series.length };
}

export const GRANULARITY_TO_GROUP_BY: Record<Granularity, BankingAnalyticsGroupBy> = {
  "Time Block": "block",
  Day: "day",
  Week: "week",
  Month: "month",
};

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function isoWeekNumber(date: Date): number {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

export function formatBucketLabel(raw: string, granularity: Granularity): string {
  if (!raw) return raw;
  const isIsoLike = ISO_DATE_RE.test(raw);
  const isoLike = raw.includes("T") ? raw : raw.replace(" ", "T");
  const date = new Date(isoLike);

  if (granularity === "Week") {
    if (isIsoLike && !Number.isNaN(date.getTime())) {
      const d = new Date(date);
      const dayOfWeek = d.getUTCDay(); // 0 is Sun
      const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const mon = new Date(d);
      mon.setUTCDate(d.getUTCDate() + diffToMon);
      const sun = new Date(mon);
      sun.setUTCDate(mon.getUTCDate() + 6);

      const monDay = mon.getUTCDate();
      const monMonth = MONTH_SHORT[mon.getUTCMonth()];
      const sunDay = sun.getUTCDate();
      const sunMonth = MONTH_SHORT[sun.getUTCMonth()];

      if (mon.getUTCMonth() === sun.getUTCMonth()) {
        return `${monDay} - ${sunDay} ${monMonth}`;
      }
      return `${monDay} ${monMonth} - ${sunDay} ${sunMonth}`;
    }
    return raw;
  }

  if (granularity === "Month") {
    if (isIsoLike && !Number.isNaN(date.getTime())) {
      return `${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
    }
    return raw;
  }

  if (granularity === "Time Block") {
    if (!Number.isNaN(date.getTime()) && raw.length > 10) {
      const hh = String(date.getUTCHours()).padStart(2, "0");
      const mm = String(date.getUTCMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    }
    return raw.length > 10 ? raw.slice(11, 16) : raw;
  }

  if (!Number.isNaN(date.getTime())) {
    const day = date.getUTCDate();
    const month = MONTH_SHORT[date.getUTCMonth()];
    return `${day} ${month}`;
  }
  return raw;
}

function diffDaysInclusive(start: string, end: string): number {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  return Math.max(
    0,
    Math.round((e.getTime() - s.getTime()) / (24 * 3600 * 1000)) + 1,
  );
}

function timeWindowHours(from: string, to: string): number {
  const parse = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return (Number.isFinite(h) ? h : 0) + (Number.isFinite(m) ? m : 0) / 60;
  };
  const span = parse(to) - parse(from);
  return span <= 0 ? 24 + span : span;
}

function summarizeReturnPeriods(periods: BankingContractPeriodResponse[]): {
  start: string;
  end: string;
  days: number;
  hoursPerDay: number;
  timeWindow: string;
  basis: "Firm" | "Non-firm";
} {
  if (periods.length === 0) {
    return {
      start: "",
      end: "",
      days: 0,
      hoursPerDay: 0,
      timeWindow: "",
      basis: "Firm",
    };
  }
  const first = periods[0];
  const start = periods
    .map((p) => p.start)
    .filter(Boolean)
    .sort()[0];
  const end =
    periods
      .map((p) => p.end)
      .filter(Boolean)
      .sort()
      .at(-1) ?? "";
  const days = periods.reduce(
    (acc, p) => acc + diffDaysInclusive(p.start, p.end),
    0,
  );
  const hoursPerDay = +timeWindowHours(first.from_time, first.to_time).toFixed(
    2,
  );
  return {
    start: start ?? first.start,
    end,
    days,
    hoursPerDay,
    timeWindow: `${first.from_time}–${first.to_time}`,
    basis: first.basis === "firm" ? "Firm" : "Non-firm",
  };
}

function summarizeSupplyPeriods(periods: BankingContractPeriodResponse[]): {
  start: string;
  end: string;
} {
  if (periods.length === 0) return { start: "", end: "" };
  const start = periods
    .map((p) => p.start)
    .filter(Boolean)
    .sort()[0];
  const end =
    periods
      .map((p) => p.end)
      .filter(Boolean)
      .sort()
      .at(-1) ?? "";
  return { start: start ?? "", end };
}

export type AnalyticsDerived = {
  contract: Contract;
  totals: Totals;
  returnRequired: number;
  avgReturn: number;
  series: SupplyPoint[];
  rows: PeriodRow[];
  blocksByDate?: Record<string, BankingContractAnalyticsBucket[]>;
};

export function deriveAnalytics(
  analytics: BankingContractAnalytics,
  granularity: Granularity = "Day",
): AnalyticsDerived {
  const { contract, metrics, daily } = analytics;
  const supplyWindow = summarizeSupplyPeriods(contract.supply_periods);
  const ret = summarizeReturnPeriods(contract.return_periods);

  const totals: Totals = {
    actual: Math.round(metrics.total_actual_mwh),
    scheduled: Math.round(metrics.total_scheduled_mwh),
    varPct:
      metrics.total_scheduled_mwh === 0
        ? 0
        : ((metrics.total_actual_mwh - metrics.total_scheduled_mwh) /
            metrics.total_scheduled_mwh) *
          100,
    days: metrics.supply_days,
  };

  const byLabel = new Map<string, { scheduled: number; actual: number; actualGna: number; actualTgna: number; approvalNos: Set<string>; cats: Set<string> }>();
  for (const d of daily) {
    const label = formatBucketLabel(d.date, granularity);
    const acc = byLabel.get(label) ?? { scheduled: 0, actual: 0, actualGna: 0, actualTgna: 0, approvalNos: new Set<string>(), cats: new Set<string>() };
    acc.scheduled += d.scheduled_mwh ?? 0;
    acc.actual += d.actual_mwh ?? 0;
    acc.actualGna += d.actual_gna ?? 0;
    acc.actualTgna += d.actual_tgna ?? 0;
    for (const a of d.approval_nos ?? []) acc.approvalNos.add(a);
    for (const c of d.categories ?? []) acc.cats.add(c);
    byLabel.set(label, acc);
  }

  let series: SupplyPoint[];
  if (granularity === "Time Block") {
    series = daily.map((d, idx) => ({
      date: String(d.time_block ?? idx + 1),
      scheduled: d.scheduled_mwh,
      actual: d.actual_mwh,
      secondaryDate: d.date,
    }));
  } else {
    series = Array.from(byLabel.entries()).map(([label, agg]) => ({
      date: label,
      scheduled: agg.scheduled,
      actual: agg.actual,
    }));
  }

  let rows: PeriodRow[] = Array.from(byLabel.entries()).map(([label, agg]) => {
    const delta = agg.actual - agg.scheduled;
    const pct = agg.scheduled === 0 ? 0 : (delta / agg.scheduled) * 100;
    const rawDate =
      daily.find((d) => formatBucketLabel(d.date, granularity) === label)?.date.slice(0, 10) || label;
    const approval_nos = [...agg.approvalNos];
    const categories = [...agg.cats];
    return {
      date: rawDate,
      label,
      scheduled: agg.scheduled,
      actual: agg.actual,
      delta,
      pct,
      actual_gna: agg.actualGna,
      actual_tgna: agg.actualTgna,
      ...(approval_nos.length > 0 ? { approval_nos } : {}),
      ...(categories.length > 0 ? { categories } : {}),
    };
  });

  if (granularity === "Day") rows = rows.slice(0, 14);

  let blocksByDate: Record<string, BankingContractAnalyticsBucket[]> | undefined;
  if (granularity === "Time Block" || granularity === "Day") {
    blocksByDate = {};
    for (const d of daily) {
      const dateKey = d.date.slice(0, 10);
      if (!blocksByDate[dateKey]) blocksByDate[dateKey] = [];
      blocksByDate[dateKey].push(d);
    }
  }

  const mappedContract: Contract = {
    id: contract.id,
    from: contract.supplier,
    to: contract.receiver,
    status: "ACTIVE",
    loa: contract.loa_number,
    bf: `${metrics.banking_factor_pct}%`,
    signed: contract.loa_date,
    windowStart: metrics.window_start || supplyWindow.start,
    windowEnd: metrics.window_end || supplyWindow.end,
    supplyPeriod: contract.supply_periods.length,
    returnPeriod: contract.return_periods.length,
    returnWindowStart: ret.start,
    returnWindowEnd: ret.end,
    returnTimeWindow: ret.timeWindow,
    returnDays: ret.days,
    returnHoursPerDay: ret.hoursPerDay,
    returnBasis: ret.basis,
    traderName: contract.traderName ?? contract.trader ?? "",
  };

  return {
    contract: mappedContract,
    totals,
    returnRequired: Math.round(metrics.return_required_mwh),
    avgReturn: +metrics.avg_return_mw.toFixed(2),
    series,
    rows,
    blocksByDate,
  };
}

export type ReturnDerived = {
  metrics: BankingContractReturnMetrics;
  series: SupplyPoint[];
  rows: PeriodRow[];
  blocksByDate?: Record<string, BankingContractAnalyticsBucket[]>;
};

export function deriveReturnAnalytics(
  analytics: BankingContractReturnAnalytics,
  granularity: Granularity = "Day",
): ReturnDerived {
  const { metrics, daily } = analytics;

  const byLabel = new Map<string, { scheduled: number; actual: number; actualGna: number; actualTgna: number; approvalNos: Set<string>; cats: Set<string> }>();
  for (const d of daily) {
    const label = formatBucketLabel(d.date, granularity);
    const acc = byLabel.get(label) ?? { scheduled: 0, actual: 0, actualGna: 0, actualTgna: 0, approvalNos: new Set<string>(), cats: new Set<string>() };
    acc.scheduled += d.scheduled_mwh ?? 0;
    acc.actual += d.actual_mwh ?? 0;
    acc.actualGna += d.actual_gna ?? 0;
    acc.actualTgna += d.actual_tgna ?? 0;
    for (const a of d.approval_nos ?? []) acc.approvalNos.add(a);
    for (const c of d.categories ?? []) acc.cats.add(c);
    byLabel.set(label, acc);
  }

  let series: SupplyPoint[];
  if (granularity === "Time Block") {
    series = daily.map((d, idx) => ({
      date: String(d.time_block ?? idx + 1),
      scheduled: d.scheduled_mwh,
      actual: d.actual_mwh,
      secondaryDate: d.date,
    }));
  } else {
    series = Array.from(byLabel.entries()).map(([label, agg]) => ({
      date: label,
      scheduled: agg.scheduled,
      actual: agg.actual,
    }));
  }

  let rows: PeriodRow[] = Array.from(byLabel.entries()).map(([label, agg]) => {
    const delta = agg.actual - agg.scheduled;
    const pct = agg.scheduled === 0 ? 0 : (delta / agg.scheduled) * 100;
    const rawDate =
      daily.find((d) => formatBucketLabel(d.date, granularity) === label)?.date.slice(0, 10) || label;
    const approval_nos = [...agg.approvalNos];
    const categories = [...agg.cats];
    return {
      date: rawDate,
      label,
      scheduled: agg.scheduled,
      actual: agg.actual,
      delta,
      pct,
      actual_gna: agg.actualGna,
      actual_tgna: agg.actualTgna,
      ...(approval_nos.length > 0 ? { approval_nos } : {}),
      ...(categories.length > 0 ? { categories } : {}),
    };
  });

  if (granularity === "Day") rows = rows.slice(0, 14);

  let blocksByDate: Record<string, BankingContractAnalyticsBucket[]> | undefined;
  if (granularity === "Time Block" || granularity === "Day") {
    blocksByDate = {};
    for (const d of daily) {
      const dateKey = d.date.slice(0, 10);
      if (!blocksByDate[dateKey]) blocksByDate[dateKey] = [];
      blocksByDate[dateKey].push(d);
    }
  }

  return { metrics, series, rows, blocksByDate };
}

export function deriveCumulativeSeries(
  daily: BankingContractAnalyticsBucket[],
  granularity: Granularity,
): SupplyPoint[] {
  if (granularity === "Time Block") {
    return daily.map((d, idx) => {
      let label: string;
      let secondaryDate: string | undefined;
      label = String(d.time_block ?? idx + 1);
      secondaryDate = d.date;
      return {
        date: label,
        scheduled: d.scheduled_mwh,
        actual: d.actual_mwh,
        secondaryDate,
      };
    });
  }

  const byLabel = new Map<string, { scheduled: number; actual: number }>();
  for (const d of daily) {
    const label = formatBucketLabel(d.date, granularity);
    const acc = byLabel.get(label) ?? { scheduled: 0, actual: 0 };
    acc.scheduled += d.scheduled_mwh ?? 0;
    acc.actual += d.actual_mwh ?? 0;
    byLabel.set(label, acc);
  }

  return Array.from(byLabel.entries()).map(([label, agg]) => ({
    date: label,
    scheduled: agg.scheduled,
    actual: agg.actual,
  }));
}

export function deriveCumulativeRows(
  daily: BankingContractAnalyticsBucket[],
  granularity: Granularity,
): PeriodRow[] {
  // If granularity is block, aggregate by unique date
  if (granularity === "Time Block") {
    const byDate = new Map<string, { scheduled: number; actual: number; actualGna: number; actualTgna: number; approvalNos: Set<string>; cats: Set<string> }>();
    for (const d of daily) {
      const acc = byDate.get(d.date) ?? { scheduled: 0, actual: 0, actualGna: 0, actualTgna: 0, approvalNos: new Set<string>(), cats: new Set<string>() };
      acc.scheduled += d.scheduled_mwh ?? 0;
      acc.actual += d.actual_mwh ?? 0;
      acc.actualGna += d.actual_gna ?? 0;
      acc.actualTgna += d.actual_tgna ?? 0;
      for (const a of d.approval_nos ?? []) acc.approvalNos.add(a);
      for (const c of d.categories ?? []) acc.cats.add(c);
      byDate.set(d.date, acc);
    }
    return Array.from(byDate.entries()).map(([date, agg]) => {
      const delta = agg.actual - agg.scheduled;
      const pct = agg.scheduled === 0 ? 0 : (delta / agg.scheduled) * 100;
      const approval_nos = [...agg.approvalNos];
      const categories = [...agg.cats];
      return {
        date,
        scheduled: agg.scheduled,
        actual: agg.actual,
        delta,
        pct,
        actual_gna: agg.actualGna,
        actual_tgna: agg.actualTgna,
        ...(approval_nos.length > 0 ? { approval_nos } : {}),
        ...(categories.length > 0 ? { categories } : {}),
      };
    });
  }

  // If granularity is Day, Week, or Month, aggregate by the formatted label
  const byLabel = new Map<string, { scheduled: number; actual: number; actualGna: number; actualTgna: number; approvalNos: Set<string>; cats: Set<string> }>();
  for (const d of daily) {
    const label = formatBucketLabel(d.date, granularity);
    const acc = byLabel.get(label) ?? { scheduled: 0, actual: 0, actualGna: 0, actualTgna: 0, approvalNos: new Set<string>(), cats: new Set<string>() };
    acc.scheduled += d.scheduled_mwh ?? 0;
    acc.actual += d.actual_mwh ?? 0;
    acc.actualGna += d.actual_gna ?? 0;
    acc.actualTgna += d.actual_tgna ?? 0;
    for (const a of d.approval_nos ?? []) acc.approvalNos.add(a);
    for (const c of d.categories ?? []) acc.cats.add(c);
    byLabel.set(label, acc);
  }

  const rows = Array.from(byLabel.entries()).map(([label, agg]) => {
    const delta = agg.actual - agg.scheduled;
    const pct = agg.scheduled === 0 ? 0 : (delta / agg.scheduled) * 100;
    const rawDate = daily.find(d => formatBucketLabel(d.date, granularity) === label)?.date.slice(0, 10) || label;
    const approval_nos = [...agg.approvalNos];
    const categories = [...agg.cats];
    return {
      date: rawDate,
      label,
      scheduled: agg.scheduled,
      actual: agg.actual,
      delta,
      pct,
      actual_gna: agg.actualGna,
      actual_tgna: agg.actualTgna,
      ...(approval_nos.length > 0 ? { approval_nos } : {}),
      ...(categories.length > 0 ? { categories } : {}),
    };
  });

  if (granularity === "Day") return rows.slice(0, 14);
  return rows;
}

export function extractBlocksByDate(
  daily: BankingContractAnalyticsBucket[],
): Record<string, BankingContractAnalyticsBucket[]> {
  const blocksByDate: Record<string, BankingContractAnalyticsBucket[]> = {};
  for (const d of daily) {
    if (!d.date) continue;
    const dateKey = d.date.slice(0, 10);
    if (!blocksByDate[dateKey]) blocksByDate[dateKey] = [];
    blocksByDate[dateKey].push(d);
  }
  return blocksByDate;
}
